import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Globe, MapPin, Loader2 } from 'lucide-react';

// ==================== COUNTRY FORM PANEL ====================
export const CountryFormPanel = ({ 
  country, 
  isOpen, 
  onClose, 
  onSubmit 
}) => {
  const { t } = useTranslation('locations');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    phone_code: '',
    currency: '',
    currency_symbol: ''
  });

  useEffect(() => {
    if (isOpen) {
      if (country) {
        setFormData({
          name: country.name || '',
          code: country.code || '',
          phone_code: country.phone_code || '',
          currency: country.currency || '',
          currency_symbol: country.currency_symbol || ''
        });
      } else {
        setFormData({
          name: '',
          code: '',
          phone_code: '',
          currency: '',
          currency_symbol: ''
        });
      }
    }
  }, [country, isOpen]);

  const handleSubmit = async () => {
    if (!formData.name || !formData.code) return;
    
    setLoading(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error saving country:', error);
    }
    setLoading(false);
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-md overflow-hidden flex flex-col p-0">
        <SheetHeader className="px-6 py-4 border-b bg-gradient-to-r from-[#E63946]/5 to-transparent">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-[#E63946]/10 flex items-center justify-center">
              <Globe className="h-6 w-6 text-[#E63946]" />
            </div>
            <div>
              <SheetTitle className="text-lg font-semibold">
                {country?.id ? t('countries.form.editTitle') : t('countries.form.title')}
              </SheetTitle>
              <SheetDescription>
                {country?.id ? t('countries.form.editDescription') : t('countries.form.description')}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1 px-6 py-4">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('countries.form.name')} *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder={t('countries.form.namePlaceholder')}
                  data-testid="country-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('countries.form.code')} *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  placeholder={t('countries.form.codePlaceholder')}
                  maxLength={3}
                  data-testid="country-code-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('countries.form.phoneCode')}</Label>
                <Input
                  value={formData.phone_code}
                  onChange={(e) => setFormData({...formData, phone_code: e.target.value})}
                  placeholder={t('countries.form.phoneCodePlaceholder')}
                  data-testid="country-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('countries.form.currency')}</Label>
                <Input
                  value={formData.currency}
                  onChange={(e) => setFormData({...formData, currency: e.target.value})}
                  placeholder={t('countries.form.currencyPlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('countries.form.symbol')}</Label>
                <Input
                  value={formData.currency_symbol}
                  onChange={(e) => setFormData({...formData, currency_symbol: e.target.value})}
                  placeholder={t('countries.form.symbolPlaceholder')}
                />
              </div>
            </div>
          </div>
        </ScrollArea>

        <div className="px-6 py-4 border-t bg-muted/30 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !formData.name || !formData.code} 
            className="bg-[#E63946] hover:bg-[#d32836]"
            data-testid="save-country-btn"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {country?.id ? t('common.update') : t('common.create')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ==================== CITY FORM PANEL ====================
export const CityFormPanel = ({ 
  city, 
  countries = [],
  isOpen, 
  onClose, 
  onSubmit 
}) => {
  const { t } = useTranslation('locations');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    country_id: '',
    state_department: '',
    postal_code: '',
    timezone: '',
    latitude: '',
    longitude: ''
  });

  useEffect(() => {
    if (isOpen) {
      if (city) {
        setFormData({
          name: city.name || '',
          country_id: city.country_id || '',
          state_department: city.state_department || '',
          postal_code: city.postal_code || '',
          timezone: city.timezone || '',
          latitude: city.latitude || '',
          longitude: city.longitude || ''
        });
      } else {
        setFormData({
          name: '',
          country_id: '',
          state_department: '',
          postal_code: '',
          timezone: '',
          latitude: '',
          longitude: ''
        });
      }
    }
  }, [city, isOpen]);

  const handleSubmit = async () => {
    if (!formData.name || !formData.country_id) return;
    
    setLoading(true);
    const submitData = {
      ...formData,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null
    };
    try {
      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('Error saving city:', error);
    }
    setLoading(false);
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-hidden flex flex-col p-0">
        <SheetHeader className="px-6 py-4 border-b bg-gradient-to-r from-[#E63946]/5 to-transparent">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-[#E63946]/10 flex items-center justify-center">
              <MapPin className="h-6 w-6 text-[#E63946]" />
            </div>
            <div>
              <SheetTitle className="text-lg font-semibold">
                {city?.id ? t('cities.form.editTitle') : t('cities.form.title')}
              </SheetTitle>
              <SheetDescription>
                {city?.id ? t('cities.form.editDescription') : t('cities.form.description')}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1 px-6 py-4">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('cities.form.name')} *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder={t('cities.form.namePlaceholder')}
                  data-testid="city-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('cities.form.country')} *</Label>
                <Select 
                  value={formData.country_id} 
                  onValueChange={(value) => setFormData({...formData, country_id: value})}
                >
                  <SelectTrigger data-testid="city-country-select">
                    <SelectValue placeholder={t('cities.form.selectCountry')} />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.filter(c => c.status === 'active').map((country) => (
                      <SelectItem key={country.id} value={country.id}>{country.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('cities.form.department')}</Label>
                <Input
                  value={formData.state_department}
                  onChange={(e) => setFormData({...formData, state_department: e.target.value})}
                  placeholder={t('cities.form.departmentPlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('cities.form.postalCode')}</Label>
                <Input
                  value={formData.postal_code}
                  onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                  placeholder={t('cities.form.postalCodePlaceholder')}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('cities.form.timezone')}</Label>
                <Input
                  value={formData.timezone}
                  onChange={(e) => setFormData({...formData, timezone: e.target.value})}
                  placeholder={t('cities.form.timezonePlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('cities.form.latitude')}</Label>
                <Input
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => setFormData({...formData, latitude: e.target.value})}
                  placeholder={t('cities.form.latitudePlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('cities.form.longitude')}</Label>
                <Input
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => setFormData({...formData, longitude: e.target.value})}
                  placeholder={t('cities.form.longitudePlaceholder')}
                />
              </div>
            </div>
          </div>
        </ScrollArea>

        <div className="px-6 py-4 border-t bg-muted/30 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !formData.name || !formData.country_id} 
            className="bg-[#E63946] hover:bg-[#d32836]"
            data-testid="save-city-btn"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {city?.id ? t('common.update') : t('common.create')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default { CountryFormPanel, CityFormPanel };
